import type {
    DisclosurePlayback,
    EligibleProduct,
    SalesAnalytics,
    SalesContextValue,
    SalesFeatures,
    SalesTimeline,
} from "@expert/sales-tools";
import type { AudioType } from "@expert/shared-types";

import {
    addGenericMessage,
    addMessage,
    addSessionSummaryMessage,
    deactivatedSummaryMessage,
    deactivatePreviousSessionSummaryMessage,
    useTimelineStore,
} from "@asurion-core/expert-workspace-timeline";
import { useCallIntroFlowQuestions, useCustomerAvatar } from "@expert/contentful";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useGaiaWebsocket } from "@expert/gaia";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getRootDispatcher } from "@/analytics";
import { getSessionContextKeysFromProducts } from "@/components/workspace/hooks/getSessionContextKeysFromProducts";
import { getBackendUrls } from "@/config/getBackendUrls";
import { useIVRInfo } from "@/customer-info/api";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { usePanelsStore } from "@/panels/modules/panels/store/panels.store";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useAgentAttributesStore } from "@/sales-tools/stores/agent-attributes.store";
import {
    isChatTask,
    isSimulatedVoiceTask,
    isVoiceTask,
    sdkEventBus,
    useActiveTask,
    useAgentSdk,
    useAgentStore,
    useLogger,
    usePartner,
    useSession,
} from "@/sdk";
import { getSharedApiClient, useFeatureFlag, userCache } from "@/shared-utils";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

import { useSetGaiaSmartOfferChecklistContext } from "./useSetGaiaSmartOfferChecklistContext";

// ─── Public API ─────────────────────────────────────────────────────────────────

/**
 * Builds a SalesContextValue from expert-ui's existing hooks and utilities.
 * Always returns a value so the WidgetContainer (rendered outside a customer
 * session) can consume context. Session/task fields are filled with safe
 * defaults when no active customer task exists.
 */
export function useBuildSalesContext(): SalesContextValue {
    const session = useSession();
    const activeTask = useActiveTask();
    const partner = usePartner();
    const partnerConfig = usePartnerConfig();
    // TEMP: isDecoupledSalesPath binding marks all logs from the decoupled tree
    // during rollout. Remove after legacy components are deleted (RDESQ-1985).
    const logger = useLogger({ module: "useBuildSalesContext", isDecoupledSalesPath: true });
    const { agentId } = useAgentStore((state) => state);
    const { features: workspaceFeatures } = useFeatures<WorkspaceFeatures>();
    const gaiaWs = useGaiaWebsocket();
    const agentSdk = useAgentSdk();

    const sessionId = session.id;
    const isVoice = isVoiceTask(activeTask);
    const isChat = isChatTask(activeTask);

    const customerAvatarUrl = useCustomerAvatar(sessionId);
    const callIntroQuestions = useCallIntroFlowQuestions(partner, { enabled: isVoice });
    const setSelectedTab = usePanelsStore((state) => state.setSelectedTab);
    const selectFlowsTab = usePanelsStore((state) => state.selectFlowsTab);
    const ivrInfo = useIVRInfo();
    const taskMdn = useTaskMdn({
        blocklistedNumbers: partnerConfig.blocklistedNumbers,
        blocklistedPrefixes: partnerConfig.blocklistedPrefixes,
    });

    const chatTaskForFeatureFlag = isChat ? activeTask : undefined;
    const { data: isChatDeviceDetectionEnabled = false } = useFeatureFlag<boolean>(
        "EWP-chat-isDeviceDetectionEnabled",
        {
            partner: chatTaskForFeatureFlag?.partner,
            topic: chatTaskForFeatureFlag?.topic,
            language: chatTaskForFeatureFlag?.language,
        },
        { isEnabled: isChat },
    );
    const [disclosurePlayback, setDisclosurePlayback] = useState<DisclosurePlayback | null>(null);
    useEffect(() => {
        const unsubStart = sdkEventBus.on("disclosure_playback_started", () => {
            setDisclosurePlayback({ state: "playing", startedAt: Date.now() });
        });
        const unsubEnd = sdkEventBus.on("disclosure_playback_ended", () => {
            setDisclosurePlayback(null);
        });
        // Cancel any in-flight disclosure when the call ends — the diff-based detection inside
        // subscribeToContactEvents may not fire after the contact terminates, so we also clear
        // playback state on hangup directly.
        const unsubCallDisconnected = sdkEventBus.on("call_disconnected", (task) => {
            setDisclosurePlayback((current) => {
                if (current) {
                    logger.debug(
                        { taskId: task?.id, reason: "call_disconnected" },
                        "clearing in-flight disclosure playback state",
                    );
                }
                return null;
            });
        });
        const unsubSession = sdkEventBus.on("session_changed", ({ action }) => {
            if (action === "end") setDisclosurePlayback(null);
        });
        return () => {
            unsubStart();
            unsubEnd();
            unsubCallDisconnected();
            unsubSession();
        };
    }, []);

    // Track productConfigs from the latest decoupled eligibility result so we can
    // derive Gaia smart-offer-checklist context keys (parity with legacy CustomerOffers).
    const [eligibilityProductConfigs, setEligibilityProductConfigs] = useState<EligibleProduct[]>([]);
    const onEligibilityResult = useCallback((productConfigs: EligibleProduct[]) => {
        setEligibilityProductConfigs(productConfigs);
    }, []);
    const sessionContextKeys = useMemo(
        () => getSessionContextKeysFromProducts(logger, eligibilityProductConfigs),
        [eligibilityProductConfigs, logger],
    );
    useSetGaiaSmartOfferChecklistContext(sessionContextKeys);

    return useMemo<SalesContextValue>(() => {
        // Match legacy CustomerPanel parity: legacy uses useTypedMostRecentTask which
        // returns the active task in training mode (Expert University) where session.kind
        // is not "with-customer". Gating on isCustomerSession here caused the decoupled
        // CustomerPanel to bail out in training. Follow the task itself instead.
        const hasSession = !!activeTask;
        const voiceTask = hasSession && isVoice ? activeTask : null;
        const chatTask = hasSession && isChat ? activeTask : null;

        return {
            sessionId,
            partner,
            partnerConfig,
            taskType: { isVoice: hasSession && isVoice, isChat: hasSession && isChat },
            taskData: hasSession
                ? {
                      customerInteractionId: activeTask.sessionId,
                      asurionCallId: voiceTask?.asurionCallId,
                      agentCallId: voiceTask?.agentCallId,
                      reservationSid: undefined, // TODO: Is this okay?
                      chatSourceApp: chatTask?.sourceApp,
                      chatTopic: chatTask?.topic,
                      chatLanguage: chatTask?.language,
                      locale: activeTask.locale,
                      mdn: taskMdn || undefined,
                      salesChannel: activeTask.type,
                  }
                : {},
            expertLocation: useAgentAttributesStore.getState().site,
            agentId,
            employeeId: userCache.employeeId ?? "",
            pilots: userCache.pilots ?? [],
            isSimulatedVoiceTask: hasSession ? isSimulatedVoiceTask(activeTask) : false,
            features: buildFeatures(partnerConfig.features, workspaceFeatures, {
                isChatDeviceDetectionEnabled,
            }),
            activeChatId: chatTask?.chatId,
            ivrInfo: hasSession && isVoice ? ivrInfo : undefined,
            callIntroQuestions,
            logger,
            apiClients: buildApiClients(sessionId),
            analytics: buildAnalytics(),
            gaiaWebsocket: gaiaWs ? { sendJsonMessage: gaiaWs.sendJsonMessage, loading: gaiaWs.loading } : undefined,
            timeline: buildTimeline(),
            customerAvatarUrl,
            startedTimestamp: session.startedTimestamp,
            onQuickRebuttal: (text) => {
                addGenericMessage(sessionId, text);
                setSelectedTab("rightBottom")("a");
            },
            onPlayDisclosure: async (audioType: AudioType) => {
                if (!voiceTask) return;
                await agentSdk.playAudioInConference(voiceTask, audioType, voiceTask.partner);
            },
            disclosurePlayback,
            onProductSelected: () => {
                selectFlowsTab();
            },
            onEligibilityResult,
            // RDESQ-1988: push Discovery/Engagement (and other) suggested prompts into the
            // Expert Assist timeline. Defaults to SalesGuidance (sales-owned bubble with title + body).
            onSendPromptToTimeline: ({ text, type = "SalesGuidance", title, items }) => {
                const metadata =
                    type === "SalesMessage" && title
                        ? { salesMessage: { title, text } }
                        : title
                          ? { salesGuidance: { title, items: items ?? [] } }
                          : undefined;
                addMessage(sessionId, {
                    id: crypto.randomUUID(),
                    text,
                    type,
                    isUnread: true,
                    status: "active",
                    key: "intentDetected",
                    metadata,
                });
            },
        };
    }, [
        activeTask,
        sessionId,
        partner,
        partnerConfig,
        isVoice,
        isChat,
        agentId,
        workspaceFeatures,
        callIntroQuestions,
        logger,
        gaiaWs,
        customerAvatarUrl,
        session.startedTimestamp,
        isChatDeviceDetectionEnabled,
        agentSdk,
        disclosurePlayback,
        setSelectedTab,
        selectFlowsTab,
        ivrInfo,
        taskMdn,
        onEligibilityResult,
    ]);
}

// ─── Feature flags ──────────────────────────────────────────────────────────────

function buildFeatures(
    partnerFeatures: ReturnType<typeof usePartnerConfig>["features"],
    workspaceFeatures: WorkspaceFeatures,
    dynamicFeatures: { isChatDeviceDetectionEnabled: boolean },
): SalesFeatures {
    return {
        isChallengesEnabled: partnerFeatures.isChallengesEnabled ?? false,
        isExpertPerformanceDashboardEnabled: partnerFeatures.isExpertPerformanceDashboardEnabled ?? false,
        isSalesQuestionnaireEnabled: partnerFeatures.isSalesQuestionnaireEnabled ?? false,
        isRealTimeComplianceCheckEnabled: partnerFeatures.isRealTimeComplianceCheckEnabled ?? false,
        isEnhancedCustomerSearchEnabled: partnerFeatures.isEnhancedCustomerSearchEnabled ?? false,
        isDigitalCompetencyInsightEnabled: partnerFeatures.isDigitalCompetencyInsightEnabled ?? false,
        isChallengeEnabled: workspaceFeatures.isChallengeEnabled,
        isChecklistSummaryEnabled: workspaceFeatures.isChecklistSummaryEnabled,
        isPreviousSessionSummaryEnabled: workspaceFeatures.isPreviousSessionSummaryEnabled ?? false,
        isDynamicSmartOfferEnabled: workspaceFeatures.isDynamicSmartOfferEnabled,
        isRapportEnabled: workspaceFeatures.isRapportEnabled,
        isPacingEnabled: workspaceFeatures.isPacingEnabled,
        isUsingMovedDiscoveryAndEngagementQuestions:
            workspaceFeatures.isUsingMovedDiscoveryAndEngagementQuestions ?? false,
        isVerizonCareEnabled: workspaceFeatures.isVerizonCareEnabled ?? false,
        isChatDeviceDetectionEnabled: dynamicFeatures.isChatDeviceDetectionEnabled,
        isAudioDisclosuresEnabled: workspaceFeatures.isAudioDisclosuresEnabled,
        isUpdatedCustomerHandoffEnabled: workspaceFeatures.isUpdatedCustomerHandoffEnabled,
    };
}

// ─── API clients ────────────────────────────────────────────────────────────────

function buildApiClients(sessionId: string): SalesContextValue["apiClients"] {
    const baseClient = getSharedApiClient(sessionId);
    const apiBaseUrl = getBackendUrls().apiBaseUrl;
    return {
        sales: baseClient.extend(() => ({ prefixUrl: `${apiBaseUrl}/api/sales` })),
        gamification: baseClient.extend(() => ({
            prefixUrl: `${apiBaseUrl}/api/gamification`,
            retry: { limit: 0 },
        })),
        customer: baseClient.extend(() => ({ prefixUrl: `${apiBaseUrl}/api/customer` })),
    };
}

// ─── Analytics adapter ──────────────────────────────────────────────────────────

function buildDispatcherAdapter(d: ReturnType<typeof getRootDispatcher>): SalesAnalytics["dispatcher"] {
    return {
        dispatchBusinessEvent: (name, data) => d.withExtra(data ?? {}).dispatchBusinessEvent(name),
        dispatchAction: (name, detail, data) => d.withExtra(data ?? {}).dispatchAction(name, detail),
        dispatchUserAction: (name, detail, data) => d.withExtra(data ?? {}).dispatchUserAction(name, detail),
        withExtra: (data) => buildDispatcherAdapter(d.withExtra(data)),
        withMeta: (data) => buildDispatcherAdapter(d.withMeta(data)),
        withIdentity: (key, value) => buildDispatcherAdapter(d.withIdentity(key, value)),
        withIdentities: (identities) => buildDispatcherAdapter(d.withIdentities(identities)),
    };
}

function buildAnalytics(): SalesAnalytics {
    return {
        dispatcher: buildDispatcherAdapter(getRootDispatcher()),
        trackFullStoryEvent: (name, data) => trackFullStoryEvent(name, data ?? {}),
        sendToHoneycomb: (_sessionId, data) =>
            getRootDispatcher().withExtra(data).dispatchBusinessEvent("HoneycombSalesEvent"),
        startDynatraceAction: (name, id) =>
            startDynatraceAction(name as Parameters<typeof startDynatraceAction>[0], id),
        endDynatraceAction: (name, error) =>
            endDynatraceAction(name as Parameters<typeof endDynatraceAction>[0], error),
    };
}

// ─── Timeline adapter ───────────────────────────────────────────────────────────

function buildTimeline(): SalesTimeline {
    return {
        getMessages: (sessionId) => useTimelineStore.getState().getTimelineState(sessionId)?.messages ?? [],
        addSessionSummaryMessage: (sessionId, data) => {
            addSessionSummaryMessage(sessionId, {
                summary: data.summary,
                header: data.header,
                timestamp: data.timestamp,
                sessionType: data.sessionType,
                resolutionStatus: data.resolutionStatus,
            });
        },
        deactivatePreviousSessionSummaryMessage,
        deactivatedSummaryMessage,
        addMessage,
    };
}
